import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Upload,
  Edit3,
  MessageSquare,
  Image as ImageIcon,
  Users,
  Eye,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useFirestoreCollection } from '../../hooks/useFirestoreCollection';
import type { ContactRequest, GalleryItem, Testimonial } from '../../types/firebase';

interface DashboardTabProps {
  onNavigate: (tab: string) => void;
}

interface AnalyticsSummary {
  daily: { date: string; sessions: number; pageViews: number; users: number }[];
  totals: { sessions: number; pageViews: number; users: number; avgDuration: number; bounceRate: number };
}

function formatGaDate(dateStr: string): string {
  if (dateStr.length !== 8) return dateStr;
  return `${dateStr.substring(6, 8)}/${dateStr.substring(4, 6)}`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ onNavigate }) => {
  const { data: requests, loading: loadingReq } = useFirestoreCollection<ContactRequest>({
    collection: 'requests',
    realtime: true,
  });

  const { data: gallery, loading: loadingGal } = useFirestoreCollection<GalleryItem>({
    collection: 'gallery',
    realtime: true,
  });

  const { data: testimonials, loading: loadingTest } = useFirestoreCollection<Testimonial>({
    collection: 'testimonials',
    realtime: true,
  });

  // Analytics mini widget
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/analytics-data?period=14');
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAnalytics(data);
    } catch {
      setAnalyticsError(true);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const newRequests = requests.filter((r) => r.status === 'new');

  const lastRequest = requests.length > 0
    ? [...requests].sort((a, b) => {
        const ta = a.createdAt?.seconds ?? 0;
        const tb = b.createdAt?.seconds ?? 0;
        return tb - ta;
      })[0]
    : null;

  const formatDate = (ts: { seconds: number } | null | undefined) => {
    if (!ts) return '--';
    return new Date(ts.seconds * 1000).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const contentStats = [
    {
      label: 'Demandes totales',
      value: loadingReq ? '...' : String(requests.length),
      sub: `dont ${newRequests.length} nouvelle(s)`,
    },
    {
      label: 'Photos galerie',
      value: loadingGal ? '...' : String(gallery.length),
      sub: `${gallery.filter((g) => g.published).length} publiee(s)`,
    },
    {
      label: 'Temoignages',
      value: loadingTest ? '...' : String(testimonials.length),
      sub: `${testimonials.filter((t) => t.published).length} publie(s)`,
    },
    {
      label: 'Derniere demande',
      value: lastRequest ? formatDate(lastRequest.createdAt) : '--',
      sub: lastRequest ? `Type : ${lastRequest.type}` : '',
    },
  ];

  const chartData = analytics?.daily.map((d) => ({
    ...d,
    date: formatGaDate(d.date),
  })) || [];

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Tableau de bord</h2>

      {/* Analytics Mini Widget */}
      {!analyticsError && (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <TrendingUp className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-semibold">Trafic (14 derniers jours)</h3>
            </div>
            <motion.button
              onClick={() => onNavigate('analytics')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-2 text-sm text-red-400 hover:text-red-300 transition-colors"
            >
              <span>Voir les analytics complets</span>
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </div>

          {analyticsLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : analytics ? (
            <>
              {/* KPIs row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <Users className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-xs text-gray-400">Visiteurs</span>
                  </div>
                  <p className="text-xl font-bold">{analytics.totals.users.toLocaleString('fr-FR')}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <Eye className="w-3.5 h-3.5 text-orange-400" />
                    <span className="text-xs text-gray-400">Pages vues</span>
                  </div>
                  <p className="text-xl font-bold">{analytics.totals.pageViews.toLocaleString('fr-FR')}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <TrendingUp className="w-3.5 h-3.5 text-green-400" />
                    <span className="text-xs text-gray-400">Sessions</span>
                  </div>
                  <p className="text-xl font-bold">{analytics.totals.sessions.toLocaleString('fr-FR')}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-xs text-gray-400">Duree moy.</span>
                  </div>
                  <p className="text-xl font-bold">{formatDuration(analytics.totals.avgDuration)}</p>
                </div>
              </div>

              {/* Mini chart */}
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="dashGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#dc2626" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" stroke="#4b5563" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis stroke="#4b5563" fontSize={10} tickLine={false} axisLine={false} width={30} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: 12,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="sessions"
                      stroke="#dc2626"
                      fill="url(#dashGrad)"
                      strokeWidth={2}
                      name="Sessions"
                    />
                    <Area
                      type="monotone"
                      dataKey="users"
                      stroke="#f97316"
                      fill="none"
                      strokeWidth={1.5}
                      strokeDasharray="4 2"
                      name="Visiteurs"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Content Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {contentStats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <p className="text-sm font-medium text-gray-600">{stat.label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
            {stat.sub && (
              <p className="text-xs text-gray-500 mt-1">{stat.sub}</p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.button
            onClick={() => onNavigate('media')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center space-x-3 p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <Upload className="w-5 h-5 text-red-600" />
            <span className="font-medium text-red-700">Mediatheque</span>
          </motion.button>
          <motion.button
            onClick={() => onNavigate('content')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <Edit3 className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-700">Contenu</span>
          </motion.button>
          <motion.button
            onClick={() => onNavigate('gallery')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <ImageIcon className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-purple-700">Galerie</span>
          </motion.button>
          <motion.button
            onClick={() => onNavigate('requests')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
          >
            <MessageSquare className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-700">
              Demandes {newRequests.length > 0 && `(${newRequests.length})`}
            </span>
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
